import React, { useState } from "react";
import { Button, Form, InputGroup, Modal } from "react-bootstrap";
import Orange_White_Border_Btn from "../../common_btn/orange_white_border_btn/Orange_White_Border_Btn";
import "./Verify_Profile_Modal.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudArrowUp, faFileImage, faXmark } from "@fortawesome/free-solid-svg-icons";
import { updateIdentityImage } from "../../../../utils/apis/login/Login";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Verify_Profile_Modal = (props) => {
  const [identityImage, setIdentityImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setIdentityImage(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError("");
    }
  };

  const onSubmit = async () => {
    if (!identityImage) {
      setError("Please upload an identity image.");
      toast.error("Please upload an identity image", { position: "bottom-right" });
      return;
    }

    setIsSubmitting(true);
    setError("");

    const finalData = new FormData();
    finalData.append("identity_image", identityImage);

    try {
      const res = await updateIdentityImage(finalData);

      if (res?.success) {
        toast.success("Identity document uploaded successfully!", {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "dark",
        });

        props?.onHide();

        if (props?.setFormSubmitted) {
          props?.setFormSubmitted(true);
        }

        if (props?.setStepCompleted) {
          props?.setStepCompleted(true);
        }

        setTimeout(() => {
          navigate("/my-account");
        }, 1000);
      } else {
        const msg = res?.message || "Failed to upload document";
        setError(msg);
        toast.error(msg, { position: "bottom-right" });
      }
    } catch (error) {
      console.error("Error uploading identity image:", error);
      const errMsg = "An error occurred. Please try again.";
      setError(errMsg);
      toast.error(errMsg, { position: "bottom-right" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="Verify_Profile_Modal">
      <ToastContainer position="bottom-right" theme="dark" limit={3} />

      <Modal
        {...props}
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="Verify_Profile_Modal premium-modal py-14"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header 
          closeButton 
          className="border-b border-cyan-900/40 bg-gradient-to-r from-slate-950 to-slate-900/95 backdrop-blur-lg"
          closeVariant="white"
        >
          <Modal.Title 
            id="contained-modal-title-vcenter"
            className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-cyan-300 to-blue-500 tracking-tight"
          >
            Verify Your Profile
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="bg-gradient-to-b from-slate-950/95 to-black/95 backdrop-blur-2xl p-8 md:p-10">
          <div className="space-y-8">
            {/* Note Section */}
            <div className="note-card bg-slate-900/70 border border-cyan-900/40 rounded-2xl p-6 backdrop-blur-md shadow-inner">
              <p className="note fw-bold text-xl text-cyan-50 mb-2">
                Verify Your Profile With ID
              </p>
              <p className="note small text-cyan-400/80">
                Build Authenticity & Security Online
              </p>
            </div>

            {/* Upload Section */}
            <div className="upload-section">
              <Form.Label className="fw-medium text-lg text-cyan-300 mb-3 block">
                Upload Your Attachment
              </Form.Label>

              <div 
                className={`upload-area relative h-64 border-2 border-dashed rounded-2xl flex flex-col items-center justify-center transition-all duration-300 cursor-pointer
                  ${error ? 'border-red-500/70 bg-red-950/20' : 'border-cyan-800/60 hover:border-cyan-400 bg-slate-900/50 hover:bg-slate-800/60'}`}
                onClick={() => document.getElementById('file-input')?.click()}
              >
                <InputGroup className="h-full w-full opacity-0 absolute inset-0 cursor-pointer">
                  <Form.Control
                    id="file-input"
                    type="file"
                    accept="image/jpeg,image/png,application/pdf"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    onChange={handleFileChange}
                    disabled={isSubmitting}
                  />
                </InputGroup>

                <FontAwesomeIcon 
                  icon={faCloudArrowUp} 
                  className="text-6xl text-cyan-500/70 mb-4" 
                />

                <p className="text-cyan-300 font-medium text-lg mb-1">
                  {identityImage ? identityImage.name : "Drag & drop or click to upload"}
                </p>
                <p className="text-sm text-cyan-400/70">
                  Supported: JPEG, PNG, PDF (max 10MB)
                </p>
              </div>

              {error && (
                <div className="mt-4 text-red-400 text-sm font-medium flex items-center gap-2 justify-center">
                  <FontAwesomeIcon icon={faXmark} className="text-red-500" />
                  {error}
                </div>
              )}

              {previewUrl && (
                <div className="preview-wrapper mt-6 rounded-2xl overflow-hidden border border-cyan-900/40 shadow-lg shadow-cyan-900/20">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-64 object-cover"
                  />
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-5 justify-center pt-6">
              <Button
                variant="outline-light"
                className="min-w-[160px] px-8 py-4 rounded-2xl border-2 border-cyan-800/60 text-cyan-300 hover:bg-cyan-950/50 hover:border-cyan-400 hover:text-cyan-200 transition-all duration-300 shadow-sm hover:shadow-cyan-500/30 text-lg font-medium"
                onClick={() => !isSubmitting && props?.onHide()}
                disabled={isSubmitting}
                style={{ cursor: isSubmitting ? "not-allowed" : "pointer" }}
              >
                Do It Later
              </Button>

              <Orange_White_Border_Btn
                btn_name={isSubmitting ? "Uploading..." : "Submit Verification"}
                onClick={onSubmit}
                disabled={isSubmitting}
                className="min-w-[220px] py-4 text-lg font-semibold rounded-2xl shadow-lg hover:shadow-orange-500/40 transition-all"
              />
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default Verify_Profile_Modal;